/**
 * Single source of truth for the numeric Richtwerte used across all 18 storage pilot pages
 * (6 kWh sizes × DE/ES/PL, pSEO-Story Task 05) - imported by every page file instead of each
 * language variant hardcoding its own numbers, same rationale as kwpSizeFacts.ts for Task 04.
 *
 * Unlike kwpSizeFacts.ts (industry rules of thumb), the self-consumption figures here are pulled
 * 1:1 from the real calculation engine's configured constants (solarbid-backend
 * application.yml, `solarbid.pv-calculation.self-consumption-*` / PvCalculationService.kt) rather
 * than invented for this content - so the numbers on these pages match what the platform's own
 * calculators actually compute, not a separate marketing estimate:
 * - self-consumption-base: 0.30 (Eigenverbrauchsanteil ohne Speicher)
 * - self-consumption-battery-bonus: 0.30 (zusätzlicher Anteil durch einen Batteriespeicher)
 * - self-consumption-cap: 0.85 (Obergrenze, auch mit Speicher + Wärmepumpe/E-Auto-Bonus)
 *
 * The battery cost range (270-1.200 EUR/kWh) is likewise not invented for this content - it is
 * the real 2026 market-research range noted in application.yml next to `battery-cost-per-kwh-eur`
 * (the sizing tool's own calibration constant, 500 EUR/kWh as its point estimate).
 */
export const SELF_CONSUMPTION_WITHOUT_BATTERY = 0.3
export const SELF_CONSUMPTION_BATTERY_BONUS = 0.3
export const SELF_CONSUMPTION_CAP = 0.85
export const SELF_CONSUMPTION_WITH_BATTERY = Math.min(
  SELF_CONSUMPTION_WITHOUT_BATTERY + SELF_CONSUMPTION_BATTERY_BONUS,
  SELF_CONSUMPTION_CAP,
)

export const BATTERY_COST_PER_KWH_RANGE_EUR = { min: 270, max: 1200 } as const

/** Reference-Anlagengröße, mit der jede Speicher-Seite kombiniert dargestellt wird (Task 05
 *  Entscheidung: Speicher-Seiten an eine feste kWp-Referenz koppeln statt in die volle
 *  Kombinationsmatrix aus Task 09 vorzugreifen) - 10 kWp, weil das laut Task 04 die meistgewählte
 *  Einfamilienhaus-Anlagengröße ist und dort im FAQ bereits als "Standardgröße mit
 *  Batteriespeicher" geführt wird. */
export const STORAGE_REFERENCE_KWP = 10

/** The 6 pilot storage sizes chosen for this rollout (task: pSEO-Story Task 05) - the full,
 *  verbindlich indexed kWh step list from Task 01 (content/landingPages/types.ts
 *  INDEXED_STORAGE_STEPS_KWH), all 6 rolled out at once per the same "voller statt reduzierter
 *  Umfang"-Entscheidung as Task 04. */
export const PILOT_STORAGE_KWH_VALUES = [5, 7, 10, 12, 15, 20] as const

/** Household-segment guidance per storage size is intentionally NOT stored here as shared prose
 *  (unlike the numeric constants above) - each language's page file writes its own translated
 *  sentence, same convention as kwpSizeFacts.ts (shared file = numbers only, per-language prose
 *  lives in each page). The underlying claim (which household segment fits which kWh size) is
 *  still meant to match across DE/ES/PL - see index.test.ts's cross-language consistency check. */
